import { useEffect, useState } from "react";
import { Ban, CheckCircle2, Flag, Loader2, MoreVertical, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { supabase } from "@/lib/supabase";
import { PageLoader } from "@/components/brand/PageLoader";
import { useAuth } from "@/lib/auth";

// -- Users: profiles roster + ban / flag controls -----------------------------

interface ProfileRow {
  id: string;
  display_name: string | null;
  role: "user" | "admin" | "venue_admin";
  flag_count: number;
  banned_until: string | null;
  created_at: string;
}

const BAN_PRESETS: Array<{ label: string; days: number | "permanent" }> = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "Permanent", days: "permanent" },
];

/**
 * Admin → Users.
 *
 * Lists every profile and exposes the moderation primitives an admin
 * needs day-to-day:
 *   * Ban (7d / 30d / permanent) by writing banned_until on the profile
 *   * Unban (clears banned_until)
 *   * Reset flag count to 0 (after we've reviewed flags + decided
 *     they're not worth a ban)
 *   * Add a manual flag (for cases an admin spotted that no player
 *     reported via the app yet)
 *
 * All actions go through audit_log via the log_audit RPC so we can
 * trace later who did what. The profiles table is RLS-gated to
 * admin-only writes (profiles_admin_all policy from the initial
 * migration), so direct supabase updates from the client are safe.
 */
export function AdminUsers() {
  const { profile: me } = useAuth();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<ProfileRow[] | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  async function load() {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, display_name, role, flag_count, banned_until, created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      toast.error(`Couldn't load users: ${error.message}`);
      setRows([]);
      return;
    }
    setRows((data ?? []) as ProfileRow[]);
  }

  useEffect(() => {
    load();
  }, []);

  // Click outside the menu closes it without taking an action.
  useEffect(() => {
    if (openMenu === null) return;
    const close = () => setOpenMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [openMenu]);

  async function logAudit(
    action: string,
    entityId: string,
    details: Record<string, unknown>,
  ) {
    // Best-effort: a failed audit write must not block the moderation
    // action itself (the ban/unban already landed by the time we get
    // here). BUT we surface failures to the console instead of
    // swallowing them silently — the previous `.catch(() => {})`
    // hid a real bug where every ban/unban/flag-reset failed to log
    // because the params below were sent with a `p_` prefix the RPC
    // doesn't have. The live signature is
    //   log_audit(action, entity_type, entity_id, details)
    // — no prefix — matching the other four admin pages.
    const { error } = await supabase.rpc("log_audit", {
      action,
      entity_type: "profile",
      entity_id: entityId,
      details,
    });
    if (error) {
      console.error("[audit] log_audit failed", action, entityId, error);
    }
  }

  async function ban(p: ProfileRow, days: number | "permanent") {
    if (p.id === me?.id) {
      return toast.error("Don't ban yourself.");
    }
    if (p.role === "admin" && days !== "permanent") {
      // Permanent ban is allowed (you might need to nuke a compromised
      // admin) but a 7-day ban on an admin is almost certainly a misclick.
      if (
        !window.confirm(
          "This user is an admin. Temporary ban won't restrict their dashboard access - only a role change does. Continue anyway?",
        )
      ) {
        return;
      }
    }
    const reason = window.prompt(
      `Reason for banning ${p.display_name ?? p.id.slice(0, 8)}? (Audit log only)`,
      "",
    );
    if (reason === null) return;
    const banned_until =
      days === "permanent"
        ? new Date("9999-12-31T23:59:59Z").toISOString()
        : new Date(Date.now() + days * 86_400_000).toISOString();

    setActingId(p.id);
    const { error } = await supabase
      .from("profiles")
      .update({ banned_until })
      .eq("id", p.id);
    setActingId(null);
    if (error) return toast.error(error.message);

    await logAudit("user.banned", p.id, {
      banned_until,
      duration: days,
      reason: reason.trim().slice(0, 500),
    });
    toast.success(
      `Banned ${days === "permanent" ? "permanently" : `for ${days} days`}.`,
    );
    await load();
  }

  async function unban(p: ProfileRow) {
    setActingId(p.id);
    const { error } = await supabase
      .from("profiles")
      .update({ banned_until: null })
      .eq("id", p.id);
    setActingId(null);
    if (error) return toast.error(error.message);
    await logAudit("user.unbanned", p.id, {});
    toast.success("Unbanned.");
    await load();
  }

  async function resetFlags(p: ProfileRow) {
    if (
      !window.confirm(
        `Clear all flags on ${p.display_name ?? p.id.slice(0, 8)}? Flag entries themselves stay in the table for history; this just zeroes their counter.`,
      )
    ) {
      return;
    }
    setActingId(p.id);
    const { error } = await supabase
      .from("profiles")
      .update({ flag_count: 0 })
      .eq("id", p.id);
    setActingId(null);
    if (error) return toast.error(error.message);
    await logAudit("user.flags_reset", p.id, {
      previous_count: p.flag_count,
    });
    toast.success("Flag count reset.");
    await load();
  }

  async function addFlag(p: ProfileRow) {
    const note = window.prompt(
      `Why are you flagging ${p.display_name ?? p.id.slice(0, 8)}? (Required, max 500 chars)`,
      "",
    );
    if (note === null) return;
    if (!note.trim()) {
      return toast.error("A reason is required for an admin-added flag.");
    }
    setActingId(p.id);
    // Manual flag = an admin-recorded report, separate from the
    // player-driven reports table. We bump flag_count directly and
    // log to audit_log so we can trace the source later.
    const { error } = await supabase
      .from("profiles")
      .update({ flag_count: p.flag_count + 1 })
      .eq("id", p.id);
    setActingId(null);
    if (error) return toast.error(error.message);
    await logAudit("user.admin_flag_added", p.id, {
      note: note.trim().slice(0, 500),
      new_count: p.flag_count + 1,
    });
    toast.success("Flag added.");
    await load();
  }

  const filtered = (rows ?? []).filter((r) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (r.display_name ?? "").toLowerCase().includes(q) ||
      r.id.toLowerCase().includes(q) ||
      r.role.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Users
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Users</h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Profile roster across the whole platform. Use the kebab menu
          to ban, unban, reset a user's flag count, or record a manual
          flag. Every action lands in the audit log.
        </p>
      </header>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name, role, or id…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {rows === null ? (
        <PageLoader />
      ) : filtered.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-card/40 py-10 text-center text-sm text-muted-foreground">
          No users match.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full min-w-[680px] text-sm">
            <thead className="border-b border-border bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Flags</th>
                <th className="px-4 py-2 text-left">Joined</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="w-10 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const banned =
                  p.banned_until && new Date(p.banned_until) > new Date();
                const isPermBan =
                  banned &&
                  p.banned_until &&
                  new Date(p.banned_until).getFullYear() > 9000;
                const acting = actingId === p.id;
                return (
                  <tr
                    key={p.id}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="px-4 py-3">
                      {p.display_name || (
                        <span className="text-muted-foreground">-</span>
                      )}
                      <p className="font-mono text-[10px] text-muted-foreground">
                        {p.id.slice(0, 8)}…
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold capitalize"
                        style={{
                          background:
                            p.role === "admin"
                              ? "rgba(255,98,0,0.15)"
                              : p.role === "venue_admin"
                                ? "rgba(96,165,250,0.15)"
                                : "rgba(136,153,166,0.15)",
                          color:
                            p.role === "admin"
                              ? "#FF6200"
                              : p.role === "venue_admin"
                                ? "#60a5fa"
                                : "#8899A6",
                        }}
                      >
                        {p.role.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          p.flag_count >= 2
                            ? "font-bold text-[#fbbf24]"
                            : p.flag_count > 0
                              ? "font-semibold"
                              : "text-muted-foreground"
                        }
                      >
                        {p.flag_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {banned ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-bold text-destructive">
                          {isPermBan ? "Banned" : `Banned · ${formatBanRemaining(p.banned_until!)}`}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-volt/10 px-2 py-0.5 text-[10px] font-bold text-volt">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="relative px-2 py-3 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenu(openMenu === p.id ? null : p.id);
                        }}
                        disabled={acting}
                        aria-label="Actions"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary disabled:opacity-50"
                      >
                        {acting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <MoreVertical className="h-4 w-4" />
                        )}
                      </button>
                      {openMenu === p.id && (
                        <div
                          className="absolute right-2 top-10 z-20 w-52 overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {!banned ? (
                            <>
                              <MenuLabel>Ban</MenuLabel>
                              {BAN_PRESETS.map((preset) => (
                                <MenuItem
                                  key={preset.label}
                                  icon={<Ban className="h-3.5 w-3.5" />}
                                  onClick={() => {
                                    setOpenMenu(null);
                                    ban(p, preset.days);
                                  }}
                                >
                                  {preset.label}
                                </MenuItem>
                              ))}
                            </>
                          ) : (
                            <MenuItem
                              icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                              onClick={() => {
                                setOpenMenu(null);
                                unban(p);
                              }}
                            >
                              Unban
                            </MenuItem>
                          )}
                          <MenuLabel>Flags</MenuLabel>
                          <MenuItem
                            icon={<Flag className="h-3.5 w-3.5" />}
                            onClick={() => {
                              setOpenMenu(null);
                              addFlag(p);
                            }}
                          >
                            Add a flag
                          </MenuItem>
                          {p.flag_count > 0 && (
                            <MenuItem
                              icon={<X className="h-3.5 w-3.5" />}
                              onClick={() => {
                                setOpenMenu(null);
                                resetFlags(p);
                              }}
                            >
                              Reset flag count
                            </MenuItem>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MenuLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="border-b border-border bg-secondary/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

function MenuItem({
  icon,
  children,
  onClick,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-secondary"
    >
      {icon}
      {children}
    </button>
  );
}

function formatBanRemaining(until: string): string {
  const ms = new Date(until).getTime() - Date.now();
  const days = Math.ceil(ms / 86_400_000);
  if (days <= 0) return "expired";
  if (days === 1) return "1 day left";
  return `${days} days left`;
}

// Keep Button import alive - it's referenced via the type imports
// chain elsewhere in this file as we expand.
void Button;

// AdminPayments moved to its own file (AdminPayments.tsx) once the
// Stripe data integration was wired up.
